/**
 * CustomContentCRUD — internal module for CustomContentService.
 *
 * Sprint 4 (#54): extracted from CustomContentService.js as part of the
 * decomposition that keeps each module under the 800-LOC limit
 * (CLAUDE.md Rule 3). Houses the core CRUD operations, library queries,
 * and the search-result cache.
 *
 * NOTE — Internal module: import from CustomContentService instead. The
 * `_customContent/` prefix signals "do not import directly". The facade
 * keeps thin instance-method wrappers so existing call sites
 * (useCustomContentLibrary, CustomLibraryTab, etc.) continue to work.
 *
 * Pattern: every function takes a `state` object that owns the per-instance
 * search cache (`cache`, `CACHE_TTL`). The facade passes `this` as state,
 * which lets tests continue to inspect `service.cache` directly.
 */

import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import {
  createNetworkError,
  createDataUnavailableError
} from '../../utils/StandardErrorFormat.js';
import { inferSectionFromCategory } from './CustomContentCategories.js';
import { getMockContentList } from './customContentMocks.js';

// Re-export so the facade can keep its single import surface.
export { getMockContentList };

// ─────────────────────────────────────────────────────────────────────────
// Cache helpers — operate on the facade's `cache` Map + `CACHE_TTL`.
// ─────────────────────────────────────────────────────────────────────────

export function getCacheKey(options) {
  const { page, limit, search, category, sortBy, sortOrder } = options;
  return `${page}:${limit}:${search}:${category}:${sortBy}:${sortOrder}`;
}

export function getCachedResult(state, cacheKey) {
  const cached = state.cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < state.CACHE_TTL) {
    return cached.data;
  }
  // Remove expired cache entry
  if (cached) {
    state.cache.delete(cacheKey);
  }
  return null;
}

export function setCachedResult(state, cacheKey, data) {
  state.cache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });

  // Cleanup old cache entries (keep cache size manageable)
  while (state.cache.size > 50) {
    const firstKey = state.cache.keys().next().value;
    state.cache.delete(firstKey);
  }
}

export function clearCache(state) {
  state.cache.clear();
}

// ─────────────────────────────────────────────────────────────────────────
// Content CRUD (each function takes (state, client, logger, ...args))
// ─────────────────────────────────────────────────────────────────────────

/**
 * Create new custom content. Resolves the section from the category if the
 * caller did not supply one. Clears the search cache on success.
 */
export async function createContent(state, client, instanceLogger, contentData) {
  try {
    instanceLogger.info('Creating content:', contentData);

    const session = await fetchAuthSession();
    const userId = session.userSub;
    instanceLogger.info('User ID:', userId);

    if (!userId) {
      return createNetworkError('User not authenticated. Please sign in to create content.');
    }

    const section = contentData.section || inferSectionFromCategory(contentData.category);

    const now = new Date().toISOString();
    const createPayload = {
      userId: userId,
      title: contentData.title,
      category: contentData.category,
      section: section, // NEW FIELD for US-040
      tags: contentData.tags || [],
      content: contentData.content,
      formattedContent: contentData.formattedContent || contentData.content,
      createdAt: now,
      updatedAt: now,
      lastUsedAt: now, // Required for composite sort key in secondary index
      usageCount: 0,
      usedInTemplates: [],
      version: 1,
      // versionHistory and metadata must be JSON strings for AWSJSON type
      versionHistory: JSON.stringify([{
        version: 1,
        content: contentData.content,
        updatedAt: now
      }]),
      metadata: JSON.stringify({
        wordCount: contentData.content.split(' ').length,
        characterCount: contentData.content.length,
        estimatedReadTime: `${Math.max(1, Math.ceil(contentData.content.split(' ').length / 200))} min read`
      })
    };

    instanceLogger.info('Create payload:', createPayload);
    const result = await client.models.CustomContent.create(createPayload);
    instanceLogger.info('Create result:', result);

    if (result.errors && result.errors.length > 0) {
      instanceLogger.error('GraphQL errors:', result.errors);
      const errorMessages = result.errors.map(e => e.message).join(', ');
      return createNetworkError(`Failed to create content: ${errorMessages}`);
    }

    if (!result.data) {
      return createNetworkError('No data returned from create mutation');
    }

    clearCache(state);

    return { content: result.data };
  } catch (error) {
    instanceLogger.error('Error creating content:', error);
    instanceLogger.error('Error details:', {
      message: error.message,
      errors: error.errors,
      stack: error.stack
    });
    return createNetworkError(error.message || 'Failed to create content', error);
  }
}

/**
 * Get list of user's custom content with filtering and pagination.
 * Optimized with DynamoDB secondary indexes + an in-memory result cache.
 */
export async function getContentList(state, client, instanceLogger, options = {}) {
  try {
    instanceLogger.info('Getting content list with options:', options);

    const {
      page = 1,
      limit = 20,
      search = '',
      category = '',
      sortBy = 'createdAt',
      sortOrder = 'desc'
    } = options;

    const cacheKey = getCacheKey(options);
    const cachedResult = getCachedResult(state, cacheKey);
    if (cachedResult) {
      instanceLogger.info('Returning cached result');
      return cachedResult;
    }

    let result;
    let content = [];

    // Use secondary index for category filtering with optimized query
    if (category && category !== 'all') {
      result = await client.models.CustomContent.listCustomContentByCategory({
        category: category,
        sortDirection: sortBy === 'createdAt' ? sortOrder.toUpperCase() : 'DESC',
        limit: search ? 1000 : limit * 2 // Fetch more if we need to filter client-side
      });
    }
    // For usage-based sorting, try to use the usage index (if available)
    else if ((sortBy === 'usageCount' || sortBy === 'lastUsedAt') && !search) {
      // Note: The usage index sorts by userId, so we need the current user's ID
      // For now, fall back to regular list if we can't optimize
      result = await client.models.CustomContent.list({
        filter: {},
        limit: limit * 2
      });
    }
    // Default: use regular list query with filters
    else {
      const filter = {};

      if (category && category !== 'all') {
        filter.category = { eq: category };
      }

      instanceLogger.info('Calling list with filter:', filter);

      const listOptions = {
        limit: search ? 500 : limit * 2, // Reduced from 1000 for better performance
        sortDirection: sortBy === 'createdAt' ? sortOrder.toUpperCase() : undefined
      };

      if (Object.keys(filter).length > 0) {
        listOptions.filter = filter;
      }

      result = await client.models.CustomContent.list(listOptions);
      instanceLogger.info('List result:', result);

      if (result.errors && result.errors.length > 0) {
        instanceLogger.error('List query errors:', result.errors);
        result.errors.forEach((err, idx) => {
          instanceLogger.error(`Error ${idx + 1}:`, err.message);
        });
      }
    }

    content = result.data || [];
    instanceLogger.info('Retrieved content count:', content.length);

    // Parse metadata, extract wordCount, and add section for each item
    content = content.map(item => {
      let wordCount = 0;
      if (item.metadata) {
        try {
          const metadata = typeof item.metadata === 'string'
            ? JSON.parse(item.metadata)
            : item.metadata;
          wordCount = metadata.wordCount || 0;
        } catch (e) {
          instanceLogger.error('Error parsing metadata:', e);
          wordCount = item.content ? item.content.split(' ').length : 0;
        }
      } else if (item.content) {
        wordCount = item.content.split(' ').length;
      }

      // US-040: Add section if missing (backward compatibility)
      const section = item.section || inferSectionFromCategory(item.category);

      if (!item.section) {
        instanceLogger.debug(`[US-040] Inferring section for item "${item.title}": category="${item.category}" → section="${section}"`);
      }

      return {
        ...item,
        section,
        wordCount
      };
    });

    if (content.length > 0) {
      instanceLogger.info('Sample item:', content[0]);
    } else {
      instanceLogger.info('No content found - checking auth...');
      const session = await fetchAuthSession();
      instanceLogger.info('Current user:', session.userSub);
    }

    // Client-side text search (only when search query provided)
    if (search) {
      const searchLower = search.toLowerCase();
      content = content.filter(item =>
        item.title?.toLowerCase().includes(searchLower) ||
        item.content?.toLowerCase().includes(searchLower) ||
        (item.tags && item.tags.some(tag => tag?.toLowerCase().includes(searchLower)))
      );
    }

    // Client-side sorting (only for non-createdAt sorts or when search is active)
    if (sortBy !== 'createdAt' || search) {
      content.sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case 'title':
            aValue = a.title?.toLowerCase() || '';
            bValue = b.title?.toLowerCase() || '';
            break;
          case 'usageCount':
            aValue = a.usageCount || 0;
            bValue = b.usageCount || 0;
            break;
          case 'lastUsedAt':
            aValue = new Date(a.lastUsedAt || 0);
            bValue = new Date(b.lastUsedAt || 0);
            break;
          default: // createdAt
            aValue = new Date(a.createdAt);
            bValue = new Date(b.createdAt);
        }

        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    // Apply pagination
    const total = content.length;
    const totalPages = Math.ceil(total / limit);
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedContent = content.slice(startIndex, endIndex);

    const response = {
      content: paginatedContent,
      pagination: {
        page,
        limit,
        total,
        totalPages
      },
      isMock: false // Real data from GraphQL backend
    };

    setCachedResult(state, cacheKey, response);

    instanceLogger.info('Returning response:', response);
    return response;
  } catch (error) {
    instanceLogger.error('Error getting content list:', error);
    instanceLogger.error('Error details:', {
      message: error.message,
      errors: error.errors,
      stack: error.stack
    });
    instanceLogger.warn('Falling back to mock data');
    return getMockContentList(options);
  }
}

/**
 * Get the most recent custom content entry for a given category.
 * Used to resolve template placeholders like {{personal_note}} and
 * {{human_touch}} from the Custom Content Library at runtime during bulk
 * generation.
 */
export async function getLatestContentByCategory(state, client, instanceLogger, category) {
  try {
    instanceLogger.info(`Fetching latest content for category: ${category}`);

    const result = await client.models.CustomContent.listCustomContentByCategory({
      category: category,
      sortDirection: 'DESC',
      limit: 1
    });

    if (result.data && result.data.length > 0) {
      instanceLogger.info(`Found custom content for "${category}": "${result.data[0].title}"`);
      return result.data[0].content;
    }

    instanceLogger.info(`No custom content found for category: ${category}`);
    return null;
  } catch (error) {
    instanceLogger.warn(`Error fetching content for category "${category}":`, error);
    return null;
  }
}

/**
 * Resolve template placeholders from Custom Content Library.
 * Maps placeholder keys to categories and fetches the most recent entry.
 */
export async function getPlaceholderContent(state, client, instanceLogger) {
  const placeholderCategoryMap = {
    'personal_note': 'Personal Note',
    'human_touch': 'Human Touch'
  };

  const resolved = {};

  for (const [placeholder, category] of Object.entries(placeholderCategoryMap)) {
    const content = await getLatestContentByCategory(state, client, instanceLogger, category);
    if (content) {
      resolved[placeholder] = content;
    }
  }

  instanceLogger.info(`Resolved ${Object.keys(resolved).length} placeholders from Custom Content Library`);
  return resolved;
}

/**
 * Get a specific content item by id.
 */
export async function getContent(state, client, instanceLogger, contentId) {
  try {
    const result = await client.models.CustomContent.get({ id: contentId });

    if (!result.data) {
      return createDataUnavailableError('Content not found');
    }

    return { content: result.data };
  } catch (error) {
    instanceLogger.error('Error getting content:', error);
    return createNetworkError('Failed to get content', error);
  }
}

/**
 * Update existing content (bumps version, appends to versionHistory).
 * Clears the search cache on success.
 */
export async function updateContent(state, client, instanceLogger, contentId, updateData) {
  try {
    const now = new Date().toISOString();

    // Get current content to update version history
    const currentResult = await client.models.CustomContent.get({ id: contentId });
    if (!currentResult.data) {
      return createDataUnavailableError('Content not found');
    }

    const currentContent = currentResult.data;
    const versionHistory = currentContent.versionHistory
      ? JSON.parse(currentContent.versionHistory)
      : [];

    versionHistory.push({
      version: currentContent.version + 1,
      content: updateData.content || currentContent.content,
      updatedAt: now
    });

    const result = await client.models.CustomContent.update({
      id: contentId,
      ...updateData,
      updatedAt: now,
      version: currentContent.version + 1,
      versionHistory: JSON.stringify(versionHistory),
      metadata: updateData.content ? JSON.stringify({
        wordCount: updateData.content.split(' ').length,
        characterCount: updateData.content.length,
        estimatedReadTime: `${Math.max(1, Math.ceil(updateData.content.split(' ').length / 200))} min read`
      }) : currentContent.metadata
    });

    clearCache(state);

    return { content: result.data };
  } catch (error) {
    instanceLogger.error('Error updating content:', error);
    return createNetworkError('Failed to update content', error);
  }
}

/**
 * Delete a content item. Clears the search cache on success.
 */
export async function deleteContent(state, client, instanceLogger, contentId) {
  try {
    const result = await client.models.CustomContent.delete({ id: contentId });

    if (!result.data) {
      return createDataUnavailableError('Content not found');
    }

    clearCache(state);

    return { message: 'Content deleted successfully' };
  } catch (error) {
    instanceLogger.error('Error deleting content:', error);
    return createNetworkError('Failed to delete content', error);
  }
}

/**
 * Track content usage (increments usageCount, sets lastUsedAt, optionally
 * appends to usedInTemplates).
 */
export async function trackUsage(state, client, instanceLogger, contentId, templateId = null) {
  try {
    const now = new Date().toISOString();

    const currentResult = await client.models.CustomContent.get({ id: contentId });
    if (!currentResult.data) {
      return createDataUnavailableError('Content not found');
    }

    const currentContent = currentResult.data;
    const usedInTemplates = currentContent.usedInTemplates || [];

    if (templateId && !usedInTemplates.includes(templateId)) {
      usedInTemplates.push(templateId);
    }

    await client.models.CustomContent.update({
      id: contentId,
      usageCount: (currentContent.usageCount || 0) + 1,
      lastUsedAt: now,
      usedInTemplates
    });

    return { message: 'Usage tracked successfully' };
  } catch (error) {
    instanceLogger.error('Error tracking usage:', error);
    return createNetworkError('Failed to track usage', error);
  }
}

/**
 * Create a custom category using the authenticated user's id (instance
 * variant — the static `CustomContentService.createCategory` takes userId
 * explicitly).
 */
export async function createCategoryInstance(state, client, instanceLogger, categoryData) {
  try {
    const session = await fetchAuthSession();
    const userId = session.userSub;

    if (!userId) {
      return createNetworkError('User not authenticated. Please sign in to create categories.');
    }

    const now = new Date().toISOString();
    const result = await client.models.CustomContentCategory.create({
      userId: userId,
      name: categoryData.name,
      description: categoryData.description,
      color: categoryData.color,
      icon: categoryData.icon,
      isDefault: false,
      createdAt: now,
      updatedAt: now
    });

    return { category: result.data };
  } catch (error) {
    instanceLogger.error('Error creating category:', error);
    return createNetworkError('Failed to create category', error);
  }
}

/**
 * Search content with advanced filters. Routes through a (currently unused)
 * `${baseUrl}/search` endpoint — preserved verbatim for API compatibility.
 */
export async function searchContent(state, client, instanceLogger, baseUrl, searchOptions = {}) {
  try {
    const {
      query = '',
      filters = {},
      sortBy = 'createdAt',
      sortOrder = 'desc'
    } = searchOptions;

    const params = new URLSearchParams({
      search: query,
      ...filters,
      sortBy,
      sortOrder
    });

    const response = await fetch(`${baseUrl}/search?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      return createNetworkError(error.error || 'Failed to search content');
    }

    return await response.json();
  } catch (error) {
    instanceLogger.error('Error searching content:', error);
    return createNetworkError('Failed to search content', error);
  }
}

/**
 * Get usage analytics from the (currently unused) `${baseUrl}/analytics`
 * endpoint — preserved verbatim.
 */
export async function getAnalytics(state, client, instanceLogger, baseUrl, period = '30d') {
  try {
    const response = await fetch(`${baseUrl}/analytics?period=${period}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      return createNetworkError(error.error || 'Failed to get analytics');
    }

    return await response.json();
  } catch (error) {
    instanceLogger.error('Error getting analytics:', error);
    return createNetworkError('Failed to get analytics', error);
  }
}

/**
 * Export content via the (currently unused) `${baseUrl}/export` endpoint —
 * preserved verbatim.
 */
export async function exportContent(state, client, instanceLogger, baseUrl, contentIds, format = 'json') {
  try {
    const params = new URLSearchParams({
      format,
      contentIds: contentIds.join(',')
    });

    const response = await fetch(`${baseUrl}/export?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      return createNetworkError(error.error || 'Failed to export content');
    }

    return await response.json();
  } catch (error) {
    instanceLogger.error('Error exporting content:', error);
    return createNetworkError('Failed to export content', error);
  }
}

/**
 * Validate that a content title is unique (currently routed through
 * searchContent — the dedicated validation endpoint is a TODO).
 */
export async function validateTitleUnique(searchContentFn, instanceLogger, title, excludeContentId = null) {
  try {
    const response = await searchContentFn({
      query: title,
      filters: { exactTitle: true }
    });

    if (excludeContentId) {
      return !response.content.some(item =>
        item.title === title && item.contentId !== excludeContentId
      );
    }

    return response.content.length === 0;
  } catch (error) {
    instanceLogger.error('Error validating title uniqueness:', error);
    return false;
  }
}

/**
 * Get aggregate content statistics. Calls the facade's `getContentList` so
 * pagination/cache behavior stays consistent.
 */
export async function getContentStats(getContentListFn, instanceLogger) {
  try {
    const response = await getContentListFn({ limit: 1000 });
    const content = response.content || [];

    const stats = {
      total: content.length,
      byCategory: {},
      recentlyUsed: 0,
      mostUsed: 0,
      averageUsage: 0
    };

    let totalUsage = 0;
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

    content.forEach(item => {
      stats.byCategory[item.category] = (stats.byCategory[item.category] || 0) + 1;

      totalUsage += item.usageCount || 0;
      stats.mostUsed = Math.max(stats.mostUsed, item.usageCount || 0);

      if (item.lastUsedAt && new Date(item.lastUsedAt) > oneWeekAgo) {
        stats.recentlyUsed++;
      }
    });

    stats.averageUsage = content.length > 0 ? totalUsage / content.length : 0;

    return stats;
  } catch (error) {
    instanceLogger.error('Error getting content stats:', error);
    return {
      total: 0,
      byCategory: {},
      recentlyUsed: 0,
      mostUsed: 0,
      averageUsage: 0
    };
  }
}

/**
 * Get available categories using the authenticated user's id (instance
 * variant — wraps the static category getter so the result is shaped as
 * `{ categories }` per the legacy API).
 */
export async function getCategoriesInstance(staticGetCategories, staticGetDefaults, instanceLogger) {
  try {
    const userAttributes = await fetchUserAttributes();
    const userId = userAttributes.sub;
    const categories = await staticGetCategories(userId);

    return { categories };
  } catch (error) {
    instanceLogger.error('Error getting categories:', error);
    const defaultCategories = staticGetDefaults();
    return { categories: defaultCategories };
  }
}
